import { useEffect, useState, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calculator, FileText, ShoppingBag, Shield, CheckCircle, Star, ArrowRight, Lock, Fingerprint, ChevronRight, Activity, AlertCircle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";
import { api } from "@/lib/api";
import { DynamicIcon } from "@/components/IconMapper";

// Lazy loaded
const Footer = lazy(() => import("@/components/Footer"));

const MobileIndex = () => {
  const [subscribeEmail, setSubscribeEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState<"success" | "error" | null>(null);
  const [subscribeMessage, setSubscribeMessage] = useState("");
  const [featuredServices, setFeaturedServices] = useState<any[]>([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await api.get("/data/packages?is_active=eq.true&category=eq.main_service&_order=created_at.desc&_limit=4");
        setFeaturedServices(data || []);
      } catch (err) {
        console.error("Failed to fetch featured services", err);
      }
    };
    fetchServices();
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscribeEmail) return;

    setIsSubscribing(true);
    setSubscribeStatus(null);
    try {
      await api.post('/data/subscribers', { email: subscribeEmail });
      setSubscribeStatus("success");
      setSubscribeMessage("Subscribed Successfully! You will now receive the latest tax updates.");
      setSubscribeEmail("");
    } catch (err: any) {
      setSubscribeStatus("error");
      const errMessage = err?.message?.toLowerCase() || '';
      if (errMessage.includes('duplicate') || errMessage.includes('unique') || err?.code === '23505') {
          setSubscribeMessage("This email is already subscribed! Try a different one.");
      } else {
          setSubscribeMessage(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-[100dvh] bg-[#f8fafc] flex flex-col font-sans text-slate-900 pb-0">
      <SEO 
        title="Make Me Filer - Easy NTN & Tax Return Filing in Pakistan" 
        description="FBR Tax Filer and NTN Registration services made easy. Over 1000+ satisfied clients."
      />
      
      <Header />

      <main className="flex-1 w-full pt-[80px]">
        
        {/* Dynamic App Hero Section with Image Background */}
        <section className="relative px-5 pt-8 pb-10 overflow-hidden bg-slate-900 rounded-b-[2.5rem] shadow-[0_15px_40px_rgba(15,23,42,0.15)] mx-2 z-10">
          <div className="absolute inset-0 w-full h-full mix-blend-overlay">
            <img 
               fetchPriority="high"
               src="/hero_office_glass_logo_1765041860613.webp" 
               alt="Hero Background" 
               className="w-full h-full object-cover object-center opacity-40 blur-[2px]" 
            />
            {/* Custom Deep Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900"></div>
          </div>
          
          <div className="absolute top-[-20%] right-[-10%] w-[300px] h-[300px] bg-blue-500/20 rounded-full blur-[60px] pointer-events-none"></div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="relative z-10">
            <div className="mt-2 mb-5 inline-flex items-center gap-1.5 bg-blue-900/40 backdrop-blur-md text-blue-200 border border-blue-500/30 px-3 py-1.5 rounded-full text-[11px] font-black tracking-wider shadow-sm">
               <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /> #1 in Azad Kashmir
            </div>

            <h1 className="text-[2.5rem] font-extrabold text-white leading-[1.05] tracking-tight mb-4">
               Tax Filing <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">Made Simple.</span>
            </h1>
            <p className="text-[15px] text-slate-300 font-medium leading-relaxed mb-6 pe-4">
               Expert tax services and FBR compliance directly on your mobile device. Fast, safe, and fully secure.
            </p>

            <Button className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white text-base font-bold shadow-[0_10px_25px_rgba(37,99,235,0.4)] active:scale-[0.98] transition-transform" asChild>
              <Link to="/tax-return-info" className="flex items-center justify-center gap-2">
                 File Tax Return <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>

            {/* Quick Trust Tags Grid - Optimized for Mobile Hero */}
            <div className="flex flex-wrap gap-2.5 mt-6 justify-start">
              <div className="flex items-center gap-1.5 bg-slate-800/60 backdrop-blur-sm px-3 py-2 rounded-[10px] border border-slate-700/50">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-200">Certified</span>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-800/60 backdrop-blur-sm px-3 py-2 rounded-[10px] border border-slate-700/50">
                <Lock className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-200">100% Secure</span>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-800/60 backdrop-blur-sm px-3 py-2 rounded-[10px] border border-slate-700/50">
                <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-200">1000+ Clients</span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Action Springboard */}
        <section className="px-5 pt-6 pb-4 bg-transparent relative z-20">
           <div className="grid grid-cols-4 gap-2.5 bg-white p-3 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100">
             <Link to="/tax-calculator" className="flex flex-col items-center justify-center gap-2 py-3 active:scale-95 transition-transform group">
                <div className="w-12 h-12 bg-indigo-50 rounded-[18px] flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                  <Calculator className="w-6 h-6 text-indigo-600" />
                </div>
                <span className="text-[11px] font-bold text-slate-700 leading-tight text-center">Calculate<br/>Tax</span>
             </Link>

             <Link to="/ntn-verification" className="flex flex-col items-center justify-center gap-2 py-3 active:scale-95 transition-transform group">
                <div className="w-12 h-12 bg-emerald-50 rounded-[18px] flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <Fingerprint className="w-6 h-6 text-emerald-600" />
                </div>
                <span className="text-[11px] font-bold text-slate-700 leading-tight text-center">Verify<br/>NTN</span>
             </Link>

             <Link to="/shop" className="flex flex-col items-center justify-center gap-2 py-3 active:scale-95 transition-transform group">
                <div className="w-12 h-12 bg-orange-50 rounded-[18px] flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                  <ShoppingBag className="w-6 h-6 text-orange-600" />
                </div>
                <span className="text-[11px] font-bold text-slate-700 leading-tight text-center">Buy<br/>Services</span>
             </Link>

             <Link to="/atl-verification" className="flex flex-col items-center justify-center gap-2 py-3 active:scale-95 transition-transform group">
                <div className="w-12 h-12 bg-sky-50 rounded-[18px] flex items-center justify-center group-hover:bg-sky-100 transition-colors">
                  <CheckCircle className="w-6 h-6 text-sky-600" />
                </div>
                <span className="text-[11px] font-bold text-slate-700 leading-tight text-center">ATL<br/>Status</span>
             </Link>
           </div>
        </section>

        {/* Highly Creative Featured Services */}
        <section className="py-6 px-4">
           <div className="flex justify-between items-end mb-6 px-1">
             <div>
               <h2 className="text-[1.35rem] font-black text-slate-900 tracking-tight leading-none mb-1.5">Our Services</h2>
               <p className="text-[13px] text-slate-500 font-medium leading-none">Tap to view expert tier FBR solutions.</p>
             </div>
             <Link to="/services" className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center bg-blue-50 px-2.5 py-1.5 rounded-lg active:bg-blue-100 transition-colors">
                All <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
             </Link>
           </div>

           <div className="grid grid-cols-1 gap-4">
             {featuredServices.map((service, idx) => (
                <div key={idx} className="relative group bg-white rounded-3xl border border-slate-100 p-5 shadow-sm hover:shadow-[0_20px_40px_rgba(37,99,235,0.08)] hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 overflow-hidden z-10 cursor-pointer">
                   {/* Background creative tile */}
                   <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-50 via-transparent to-transparent -z-10 rounded-bl-[100px] group-hover:from-blue-100/80 transition-colors duration-500" />
                   
                   <div className="flex gap-4">
                     <div className="w-[52px] h-[52px] bg-blue-50 border border-blue-100/50 rounded-2xl flex-shrink-0 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300 relative shadow-sm group-hover:shadow-md">
                       <span className="absolute inset-0 bg-blue-600 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300 shadow-inner"></span>
                       {service.icon_name ? <DynamicIcon name={service.icon_name} className="w-[26px] h-[26px] text-blue-600 group-hover:text-white relative z-10 transition-colors" /> : <Activity className="w-[26px] h-[26px] text-blue-600 group-hover:text-white relative z-10 transition-colors" />}
                     </div>
                     <div className="flex-1">
                       <h3 className="text-[15px] font-black text-slate-900 mb-1 leading-tight group-hover:text-blue-700 transition-colors">{service.name}</h3>
                       <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 pr-2 group-hover:text-slate-600 transition-colors">{service.description}</p>
                     </div>
                   </div>
                   
                   <div className="mt-4 pt-4 border-t border-slate-50 flex items-center justify-between group-hover:border-slate-100 transition-colors">
                     <div className="flex items-end gap-1">
                       <span className="text-sm font-black text-blue-600 border border-blue-100 bg-blue-50 px-3 py-1.5 rounded-[10px] group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-700 transition-colors duration-300 shadow-sm">PKR {service.price?.toLocaleString()}{(service.name || '').toLowerCase().includes('bookkeeping') || (service.name || '').toLowerCase().includes('sales tax') ? '/mo' : ''}</span>
                     </div>
                     <Link to={`/checkout?service=${service.id}`} aria-label={`Checkout - ${service.name}`} className="inline-flex h-9 w-9 rounded-full bg-slate-100 text-slate-600 items-center justify-center group-hover:bg-blue-600 group-hover:text-white group-hover:shadow-lg group-hover:shadow-blue-600/30 transition-all duration-300">
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                     </Link>
                   </div>
                </div>
             ))}

             {/* Skeletons while loading */}
             {featuredServices.length === 0 && Array.from({length: 3}).map((_, i) => (
                <div key={`skel-${i}`} className="w-full h-[140px] bg-white rounded-3xl border border-slate-100 animate-pulse shadow-sm" />
             ))}
           </div>
        </section>

        {/* Enhanced Newsletter / CTA */}
        <section className="px-5 py-10 pb-16">
           <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] text-white w-full rounded-[2.5rem] p-6 sm:p-8 relative overflow-hidden shadow-[0_20px_50px_rgba(15,23,42,0.3)]">
             {/* Decorative glows */}
             <div className="absolute top-[-20%] left-[-10%] w-[150px] h-[150px] bg-blue-500/30 blur-[40px] rounded-full pointer-events-none" />
             <div className="absolute bottom-[-20%] right-[-10%] w-[180px] h-[180px] bg-indigo-500/20 blur-[50px] rounded-full pointer-events-none" />
             
             <div className="relative z-10">
               <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-5 border border-white/10">
                 <Lock className="w-6 h-6 text-blue-300" />
               </div>
               <h2 className="text-[22px] font-extrabold mb-2 tracking-tight">Stay Updated</h2>
               <p className="text-[13px] text-slate-300 mb-6 leading-relaxed">Join 5000+ professionals receiving priority FBR updates and tax deadline intelligence automatically.</p>
               
               <form onSubmit={handleSubscribe} className="flex flex-col gap-3 w-full">
                 <div className="flex gap-2 w-full">
                   <Input 
                     className="flex-1 h-[52px] bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-400 rounded-[14px] px-4 font-medium" 
                     placeholder="Your email address" 
                     type="email"
                     value={subscribeEmail}
                     onChange={(e) => setSubscribeEmail(e.target.value)}
                     required 
                   />
                   <Button type="submit" aria-label="Subscribe to newsletter" disabled={isSubscribing} className="h-[52px] w-[52px] rounded-[14px] bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-600/20 px-0 flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform">
                      <ArrowRight className="w-[22px] h-[22px] text-white" />
                   </Button>
                 </div>
                 
                 {/* Explicit Success/Error Messaging */}
                 {subscribeStatus && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`mt-1 p-3 rounded-xl flex items-start gap-2.5 ${subscribeStatus === 'success' ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                       {subscribeStatus === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />}
                       <p className="text-[12px] font-bold text-slate-200 leading-tight">{subscribeMessage}</p>
                    </motion.div>
                 )}
               </form>
             </div>
           </div>
        </section>

      </main>

      <Suspense fallback={<div className="h-24 bg-slate-100 w-full animate-pulse" />}>
         <Footer />
      </Suspense>
      
    </div>
  );
};

export default MobileIndex;
